import React from 'react';
import { css } from '@emotion/react';
import usePrimaryNav from '../../../hooks/use-primary-nav';
import { useMicrosite } from '../../../contexts/franchise/microsite-context';
import { colors } from '../../primitives/colors';
import Link from '../../primitives/link';
import Logo from '../../primitives/logo';
import Container from '../../primitives/grid/container';
import { Drawer, DrawerContent } from '../../primitives/drawer';
import DirectionTransition from '../../animations/transitions/direction-transition';
import NavIcon from '../../../images/svg/header-nav.svg';
import Typography from '../../primitives/typography';
import SiteSearchBar from '../../organisms/site-search-input';
import { PHONE_NUMBER_DISPLAY } from '../../../constants/constants';
import PhoneNumber from '../../molecules/phone-number';
import Divider from '../../primitives/divider';
import ArrowIcon from '../../../images/svg/arrow.svg';
import SvgSafeGatsbyImage from '../../atoms/SvgSafeGatsbyImage';
import CloseButton from '../../atoms/CloseButton';
import MobileCta from './mobile-cta';
import triggerInvoca from '../../utils/trigger-invoca';
import MobileLocationDrawer from './mobile-location-drawer';
import { MenuCategory, MenuNavItem } from '../types';

const styles = {
    drawerContentOuterContainer: css({
        flexGrow: '1',
        width: '100%',
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflowY: 'scroll',
        WebkitOverflowScrolling: 'touch',
        overflowX: 'hidden',
        height: '100%',
        backgroundColor: colors.primaryOrange,
        color: colors.shadesWhite,
    }),
    drawerWidthContainer: css({
        width: '100%',
    }),
    closeButtonContainer: css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        paddingTop: '24px',
    }),
    closeButton: css({
        position: 'relative',
    }),
    directionTransition: css({
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        width: '100%',
        paddingBottom: '24px',
    }),
    directionTransitionContainer: css({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    }),
    menuTitleContainer: css({
        paddingTop: '16px',
        backgroundColor: colors.primaryOrange,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    }),
    backButton: css({
        display: 'inline-flex',
        gap: '24px',
        alignItems: 'center',
    }),
    backButtonFill: css({
        svg: {
            fill: 'white',
            height: '18px',
        },
    }),
    divider: css({
        opacity: 0.4,
        marginTop: '16px',
    }),
    menuContainer: css({
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        // height: '100%',
        rowGap: '16px',
        paddingTop: '16px',
        paddingBottom: '16px',
    }),
    childMenuLink: css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    }),
    childMenuLinkIcon: css({
        width: '32px',
        height: '32px',
    }),
    subgroupList: css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    }),
    subgroupItemTypography: css({
        display: 'inline-block',
        width: '100%',
    }),
    subgroupListItem: css({
        width: '100%',
    }),
    subgroupListItemTypographyAlt: css({
        display: 'inline-block',
        width: '100%',
    }),
    dividerAlt: css({
        marginTop: '32px',
        opacity: 0.4,
    }),
    siteSearchBar: css({
        width: '100%',
        marginTop: '32px',
    }),
    bylineContainer: css({
        marginTop: '18px',
    }),
    numberContainer: css({
        marginTop: '4px',
    }),
    ctaSticky: css({
        position: 'sticky',
        bottom: '0',
        width: '100%',
        zIndex: 250,
        boxShadow: '0 -2px 12px 0 #0001',
    }),
    headerOuterContainer: css({
        backgroundColor: colors.shadesWhite,
    }),
    headerInnerContainer: css({
        display: 'flex',
        padding: '11px 0',
        justifyContent: 'space-between',
        height: '100%',
    }),
    logoLink: css({
        alignSelf: 'center',
        position: 'relative',
    }),
    logo: css({
        width: '184px',
        transition: '0.2s',
        transformOrigin: 'bottom left',
    }),
    fixedCta: css({
        maxWidth: '100vw',
        fontSize: '16px',
        zIndex: 250,
        position: 'fixed',
        bottom: '0',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 -2px 12px 0 #0001',
        transitionProperty: 'color, background-color, border-color, text-decoration-color, fill, stroke',
        transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
        transitionDuration: '300ms',
        transition: 'transform 0.15s ease-in',
        transform: 'translate3d(0px, 60px, 0px)',
        '.scroll-up &': {
            transform: 'none',
        },
        '.microsite-page &, .microsite-service &, .metro-state &, .locations-index &': {
            transform: 'none',
        },
        '&.mobile-cta-open': {
            transform: 'none',
        },
    }),
};

const MobileHeader = (): JSX.Element => {
    const { nav_items: primaryNavLinks } = usePrimaryNav();
    const {
        isMicrosite,
        permalink: micrositePermalink,
        menu: micrositeMenu,
    } = useMicrosite();

    const menuToUse: MenuNavItem[] = (isMicrosite && micrositeMenu) ? micrositeMenu.nav_items : primaryNavLinks;

    const drawerRef = React.useRef(null);

    const [open, setOpen] = React.useState(false);
    const [currentMenu, setCurrentMenu] = React.useState<MenuNavItem[]|MenuCategory[]>(menuToUse);
    const [menuDirection, setMenuDirection] = React.useState<'forward'|'backward'>('forward');
    const [menuChild, setMenuChild] = React.useState<string|null>(null);

    const [locationDrawerOpen, setLocationDrawerOpen] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setMenuDirection('forward');
            setCurrentMenu(menuToUse);
            setMenuChild(null);
        }
    }, [menuToUse, open]);

    React.useEffect(() => {
        if (isMicrosite && micrositeMenu) {
            setCurrentMenu(micrositeMenu.nav_items);
            setMenuChild(null);
        } else {
            setCurrentMenu(primaryNavLinks);
            setMenuChild(null);
        }
    }, [isMicrosite, micrositeMenu, primaryNavLinks]);

    React.useEffect(() => {
        if (open) {
            triggerInvoca();
        }
    }, [open]);

    return (
        <>
            <div ref={drawerRef} />
            {drawerRef?.current && (
                <Drawer open={open}>
                    <DrawerContent placement="top" size="full" container={drawerRef?.current || undefined} animation="fade">
                        <div css={styles.drawerContentOuterContainer}>
                            <div css={styles.drawerWidthContainer}>
                                <Container>
                                    <div css={styles.closeButtonContainer}>
                                        <CloseButton
                                            css={styles.closeButton}
                                            onClick={() => {
                                                setOpen(false);
                                            }}
                                        />
                                    </div>
                                </Container>
                                <DirectionTransition
                                    shouldChange={JSON.stringify(currentMenu)}
                                    direction={menuDirection}
                                    css={styles.directionTransition}
                                >
                                    <Container css={styles.directionTransitionContainer}>
                                        {menuChild && (
                                            <div css={styles.menuTitleContainer}>
                                                <button
                                                    type="button"
                                                    aria-label="Go Back to Main Menu"
                                                    css={styles.backButton}
                                                    onClick={() => {
                                                        setMenuDirection('backward');
                                                        setCurrentMenu(menuToUse);
                                                        setMenuChild(null);
                                                    }}
                                                >
                                                    <span css={styles.backButtonFill}>
                                                        <ArrowIcon />
                                                    </span>
                                                    <Typography as="span" fontSize="xl">
                                                        {menuChild}
                                                    </Typography>
                                                </button>
                                                <Divider css={styles.divider} />
                                            </div>
                                        )}
                                        <div css={styles.menuContainer}>
                                            {currentMenu.map((item) => {
                                                if (!item) return null;

                                                if ('nav_item_link' in item) {
                                                    const { nav_item_link, columns } = item;
                                                    if (!nav_item_link) return null;
                                                    const { href, title } = nav_item_link;
                                                    const childMenu = columns ? columns.flatMap(x => x.group) : [];

                                                    if (!(childMenu.length > 0)) {
                                                        return (
                                                            <Link
                                                                to={href}
                                                                css={styles.childMenuLink}
                                                            >
                                                                <Typography
                                                                    fontSize='4xl'
                                                                >
                                                                    {title}
                                                                </Typography>
                                                            </Link>
                                                        );
                                                    }

                                                    return (
                                                        <div>
                                                            <Typography
                                                                fontSize="4xl"
                                                                as="button"
                                                                onClick={() => {
                                                                    setMenuDirection('forward');
                                                                    setCurrentMenu(childMenu);
                                                                    setMenuChild(title);
                                                                }}
                                                            >
                                                                {title}
                                                                {' '}
                                                                +
                                                            </Typography>
                                                        </div>
                                                    );
                                                }

                                                const {
                                                    category_link, subgroup: childGroup, icon_inverse,
                                                } = item;

                                                if (!category_link) return null;
                                                const { href, title } = category_link;

                                                if (!(childGroup.length > 0)) {
                                                    return (
                                                        <Link
                                                            to={href}
                                                            css={styles.childMenuLink}
                                                        >
                                                            {icon_inverse && (
                                                                <SvgSafeGatsbyImage
                                                                    css={styles.childMenuLinkIcon}
                                                                    image={icon_inverse}
                                                                />
                                                            )}
                                                            <Typography
                                                                fontSize={childGroup ? '2xl' : '4xl'}
                                                            >
                                                                {title}
                                                            </Typography>
                                                        </Link>
                                                    );
                                                }

                                                if (childGroup) {
                                                    return (
                                                        <>
                                                            <Link
                                                                to={href}
                                                                css={styles.childMenuLink}
                                                            >
                                                                {icon_inverse && (
                                                                    <SvgSafeGatsbyImage
                                                                        css={styles.childMenuLinkIcon}
                                                                        image={icon_inverse}
                                                                    />
                                                                )}
                                                                <Typography
                                                                    fontSize={childGroup ? '2xl' : '4xl'}
                                                                >
                                                                    {title}
                                                                </Typography>
                                                            </Link>
                                                            {childGroup.map((subgroupItem) => {
                                                                if (!subgroupItem) return null;

                                                                const { subgroup_link, links } = subgroupItem;

                                                                const { title } = subgroup_link;

                                                                if (title) {
                                                                    return (
                                                                        <>
                                                                            <Typography
                                                                                fontSize={{
                                                                                    dt: 'lg',
                                                                                }}
                                                                                fontWeight="medium"
                                                                                style={{
                                                                                    marginLeft: icon_inverse ? '44px' : '24px',
                                                                                }}
                                                                            >
                                                                                {title}
                                                                            </Typography>
                                                                            <ul
                                                                                css={styles.subgroupList}
                                                                                style={{
                                                                                    marginLeft: icon_inverse ? '68px' : '44px',
                                                                                }}
                                                                            >
                                                                                {links.map((link) => {
                                                                                    if (!link) return null;

                                                                                    const { href, title } = link;

                                                                                    return (
                                                                                        <li>
                                                                                            <Typography
                                                                                                as={Link}
                                                                                                to={href}
                                                                                                fontSize={{
                                                                                                    dt: 'lg',
                                                                                                }}
                                                                                                css={styles.subgroupItemTypography}
                                                                                            >
                                                                                                {title}
                                                                                            </Typography>
                                                                                        </li>
                                                                                    );
                                                                                })}
                                                                            </ul>
                                                                        </>
                                                                    );
                                                                }

                                                                return (
                                                                    <ul
                                                                        css={styles.subgroupList}
                                                                        style={{
                                                                            marginLeft: icon_inverse ? '44px' : '24px',
                                                                        }}
                                                                    >
                                                                        {links.map((link) => {
                                                                            if (!link) return null;

                                                                            const { href, title } = link;

                                                                            return (
                                                                                <li css={styles.subgroupListItem}>
                                                                                    <Typography
                                                                                        as={Link}
                                                                                        to={href}
                                                                                        fontSize={{
                                                                                            dt: 'lg',
                                                                                        }}
                                                                                        css={styles.subgroupListItemTypographyAlt}
                                                                                    >
                                                                                        {title}
                                                                                    </Typography>
                                                                                </li>
                                                                            );
                                                                        })}
                                                                    </ul>
                                                                );
                                                            })}
                                                        </>
                                                    );
                                                }

                                                return (
                                                    <div>
                                                        <Typography
                                                            fontSize="4xl"
                                                            as="button"
                                                            onClick={() => {
                                                                setMenuDirection('forward');
                                                                setCurrentMenu(childGroup);
                                                                setMenuChild(title);
                                                            }}
                                                        >
                                                            {title}
                                                            {' '}
                                                            +
                                                        </Typography>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {!isMicrosite && (
                                            <>
                                                <Divider css={styles.dividerAlt} />
                                                <div
                                                    css={styles.siteSearchBar}
                                                >
                                                    <SiteSearchBar />
                                                </div>
                                                <div css={styles.bylineContainer}>
                                                    24/7 Nationwide Service
                                                    {' '}
                                                    <div css={styles.numberContainer}>
                                                        <Typography as={PhoneNumber} fontSize="2xl">
                                                            {PHONE_NUMBER_DISPLAY}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </Container>
                                </DirectionTransition>
                            </div>
                            <div css={styles.ctaSticky}>
                                <MobileCta inverse setOpen={setLocationDrawerOpen} />
                            </div>
                        </div>
                    </DrawerContent>
                </Drawer>
            )}
            <MobileLocationDrawer open={locationDrawerOpen} setOpen={setLocationDrawerOpen} />
            <Container css={styles.headerOuterContainer}>
                <div css={styles.headerInnerContainer}>
                    <Link
                        to={(isMicrosite && micrositePermalink) ? micrositePermalink : '/'}
                        css={styles.logoLink}
                        aria-label="SERVPRO"
                    >
                        <Logo css={styles.logo} />
                    </Link>
                    <button
                        type="button"
                        onClick={() => {
                            setOpen(true);
                        }}
                    >
                        <NavIcon />
                        <span>Menu</span>
                    </button>
                </div>
            </Container>
            <div css={styles.fixedCta}>
                <MobileCta setOpen={setLocationDrawerOpen} />
            </div>
        </>
    );
};

export default MobileHeader;
